import { Flag } from '@components/display/images';
import { Button, CloseButton } from '@components/utility/buttons';
import { InputField } from '@components/utility/inputs';
import { Select, type Option } from '@components/utility/selects';
import { useInboxContext } from '@inbox/contexts/InboxContext';
import { InboxSchema } from '@inbox/schemas/InboxSchema';
import type { IInbox, InboxForm, InboxModalType } from '@inbox/types/inbox';
import {
    handleAddError,
    handleAddSuccess,
    handleEditError,
    handleEditSuccess,
    handleNoInboxIdError,
} from '@inbox/utils/';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import { Dialog, DialogActions, DialogContent, DialogTitle, ListItemText, Typography } from '@mui/material';
import { clsx } from 'clsx';
import { useFormik } from 'formik';
import { useCallback, useEffect, useMemo } from 'react';

interface InboxModalProps {
    open: boolean;
    mode: InboxModalType;
    selectedInbox?: Partial<IInbox>;
    onClose: () => void;
}

export const InboxModal = ({ open, mode, selectedInbox, onClose }: InboxModalProps) => {
    const { onAddInbox, onUpdateInbox, managedCountries } = useInboxContext();

    const dropdownCountries: Option[] = useMemo(() => {
        if (!managedCountries) return [];
        return managedCountries.map((country) => ({
            label: country.name,
            value: country.code,
        }));
    }, [managedCountries]);

    const title = mode === 'add' ? 'Create Sales Inbox' : 'Edit Sales Inbox';
    const subheader =
        mode === 'add'
            ? 'Add a new sales inbox and associate it with countries'
            : 'Update inbox details and country associations';

    const buttonClassName = 'flex-1';
    const paddingClassName = 'px-16';

    const getInitialValuesFromData = (data: Partial<IInbox> | undefined): InboxForm => {
        if (!data) {
            return {
                email: '',
                country_codes: [],
                notes: '',
            };
        }

        return {
            email: data.email ?? '',
            country_codes: data.countries?.map((country) => country.code) ?? [],
            notes: data.notes ?? '',
        };
    };

    const handleSubmitAddInbox = async (values: InboxForm) => {
        try {
            await onAddInbox(values);
            handleAddSuccess({ email: values.email });
            resetForm();
            onClose();
        } catch (error) {
            handleAddError(error, values);
        }
    };

    const handleSubmitEditInbox = async (values: InboxForm) => {
        if (!selectedInbox || !selectedInbox?.id) {
            handleNoInboxIdError();
            return;
        }
        try {
            await onUpdateInbox(values);
            handleEditSuccess({ email: values.email });
            resetForm();
            onClose();
        } catch (error) {
            handleEditError(error, values);
        }
    };

    const handleSubmitAddOrEdit = async (values: InboxForm) => {
        if (mode === 'add') {
            await handleSubmitAddInbox(values);
        } else if (mode === 'edit') {
            await handleSubmitEditInbox(values);
        }
    };

    const { values, touched, errors, resetForm, handleSubmit, handleChange } = useFormik<InboxForm>({
        initialValues: getInitialValuesFromData(selectedInbox),
        validationSchema: InboxSchema,
        onSubmit: handleSubmitAddOrEdit,
    });

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const renderCountryOption = useCallback(
        (item: Option) => (
            <ListItemText
                className="pt-1"
                slotProps={{
                    primary: {
                        className: '!flex items-center gap-2 !text-sm',
                    },
                }}
            >
                <Flag className="w-[27px] h-[15px] rounded-sm" flag={(item.value as string).toLowerCase()} />
                <p>{item.label}</p>
            </ListItemText>
        ),
        []
    );

    useEffect(() => {
        if (selectedInbox) {
            resetForm({ values: getInitialValuesFromData(selectedInbox) });
        } else {
            resetForm({ values: getInitialValuesFromData(undefined) });
        }
    }, [selectedInbox, resetForm]);

    return (
        <form onSubmit={handleSubmit} noValidate>
            <Dialog
                open={open}
                onClose={handleClose}
                slotProps={{ paper: { className: 'py-12 pb-6 rounded-xl' } }}
                maxWidth={false}
                className="max-w-full md:max-w-[900px] mx-auto"
                disablePortal
            >
                <DialogTitle component={Typography} variant="h4" fontWeight={500} className="text-center font-museo">
                    {title}
                </DialogTitle>
                <CloseButton onClick={handleClose} />
                <DialogContent className={clsx('flex flex-col gap-4 pt-0', paddingClassName)}>
                    <Typography variant="subtitle1" className="text-center mb-4">
                        {subheader}
                    </Typography>
                    <InputField
                        required
                        label="Email"
                        name="email"
                        placeholder="Email"
                        value={values.email}
                        onChange={handleChange}
                        fullWidth
                        variant="outlined"
                        error={touched.email && Boolean(errors.email)}
                        helperText={touched.email && errors.email}
                    />
                    <Select
                        required
                        searchable
                        multiple
                        name="country_codes"
                        size="small"
                        label="Country"
                        items={dropdownCountries ?? []}
                        fcSX={{ size: 'small' }}
                        renderValue={(selected) => <div>{(selected as [])?.length} selected</div>}
                        renderOption={renderCountryOption}
                        onChange={handleChange}
                        value={values.country_codes}
                        virtualize
                        fullWidth
                        error={touched.country_codes && Boolean(errors.country_codes)}
                        helperText={touched.country_codes && errors.country_codes}
                    />
                    <InputField
                        label="Notes"
                        placeholder="Notes"
                        name="notes"
                        value={values.notes}
                        onChange={handleChange}
                        fullWidth
                        variant="outlined"
                        multiline
                        rows={4}
                    />
                </DialogContent>
                <DialogActions className={`w-full flex justify-between ${paddingClassName}`}>
                    <Button.Neutral
                        startIcon={<CloseIcon />}
                        onClick={handleClose}
                        fullRounded
                        fullWidth
                        className="bg-neutral-650 black-border text-black h-full"
                        size="medium"
                    >
                        Cancel
                    </Button.Neutral>
                    <Button.Primary
                        startIcon={<CheckCircleOutlinedIcon />}
                        variant="contained"
                        type="submit"
                        fullWidth
                        fullRounded
                    >
                        Confirm
                    </Button.Primary>
                </DialogActions>
            </Dialog>
        </form>
    );
};
